import * as Yup from "yup";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import TrackerName from "./FinancingRequestStudentDataTrackerName";
import FinancingRequestStudentData from "./FinancingRequestStudentData";
import { TransportOptionBase } from "../transport-option/TransportOption";
import { TransportOptionType } from "../../transport-option-type/TransportOptionType";
import SchoolCarrierTransportOption from "../transport-option/SchoolCarrierTransportOption";
import AuthorizedCarrierTransportOption from "../transport-option/AuthorizedCarrierTransportOption";
import CompanionInPublicTransportOption from "../transport-option/CompanionInPublicTransportOption";
import PersonalTransportOption from "../transport-option/PersonalTransportOption";
import FoodOption from "../FoodOption";
import ElementarySchoolTeachingAidOption from "../teaching-aid-option/ElementarySchoolTeachingAidOption";
import HighSchoolTeachingAidOption from "../teaching-aid-option/HighSchoolTeachingAidOption";
import { SchoolCarrier } from "../SchoolCarrier";
import { AuthorizedCarrier } from "../AuthorizedCarrier";

const defineStudentDataValidationConfig = (
  schoolCarriers: SchoolCarrier[],
  authorizedCarriers: AuthorizedCarrier[]
): ValidationConfiguration => {
  const config = {
    trackerName: TrackerName.StudentsData,
    validationScheme: Yup.array()
      .of(
        Yup.object<FinancingRequestStudentData[]>()
          .shape({
            transportOption: defineTransportOptionSchema(),
            foodOption: defineFoodSchema(),
            teachingAidOption: defineTeachingAidSchema(),
          })
          .test(
            "StudentData",
            "Barem jedna opcija mora biti odabrana",
            function (value: FinancingRequestStudentData) {
              const { path, createError, parent } = this;

              const financingTest = defineFinancingRequestTest(
                value,
                path,
                createError
              );
              const foodTest = defineFoodTest(value, path, createError);
              const teachingAidTest = defineTeachingAidTest(
                value,
                path,
                createError
              );

              if (financingTest) return financingTest;
              if (foodTest) return foodTest;
              if (teachingAidTest) return teachingAidTest;
              return true;
            }
          )
      )
      .test(
        "StudentData",
        "Nije odabran odgovarajući broj prijevoza",
        function (studentsData: FinancingRequestStudentData[]) {
          const { path, createError, parent } = this;

          let schoolTransportTest = defineSchoolTransportTest(
            studentsData,
            schoolCarriers,
            path,
            createError
          );

          let authorizedTransportTest = defineAuthorizedTransportTest(
            studentsData,
            authorizedCarriers,
            path,
            createError
          );

          if (schoolTransportTest) return schoolTransportTest;
          if (authorizedTransportTest) return authorizedTransportTest;
          return true;
        }
      ),
  } as ValidationConfiguration;

  return config;
};

const defineTransportOptionSchema = () => {
  return Yup.lazy(
    (
      value: (
        | SchoolCarrierTransportOption
        | PersonalTransportOption
        | AuthorizedCarrierTransportOption
        | CompanionInPublicTransportOption
      ) &
        TransportOptionBase
    ) => {
      if (value) {
        let objectSchema = Yup.object().shape({
          transportOptionType: Yup.number().nullable().required(),
        });

        switch (value.transportOptionType) {
          case TransportOptionType.SchoolCarrier:
            objectSchema = objectSchema.concat(
              Yup.object().shape({
                vehicleType: Yup.number().nullable().required(),
                registrationPlate: Yup.string().nullable().required(),
              })
            );
            break;
          case TransportOptionType.Personal:
            objectSchema = objectSchema.concat(
              Yup.object().shape({
                mileageFromHomeToSchool: Yup.number()
                  .min(0.2, "Kilometri ne mogu biti manji od 0,2.")
                  .nullable()
                  .required(),
              })
            );
            break;
          case TransportOptionType.AuthorizedCarrier:
            objectSchema = objectSchema.concat(
              Yup.object().shape({
                authorizedCarrierId: Yup.number().nullable().required(),
              })
            );
            break;
          case TransportOptionType.CompanionInPublicTransport:
            objectSchema = objectSchema.concat(
              Yup.object().shape({
                companionInPublicTransportType: Yup.number()
                  .nullable()
                  .required(),
                ticketPrice: Yup.number().nullable().required(),
              })
            );
            break;
          default:
            break;
        }

        return objectSchema;
      }

      return Yup.mixed().notRequired();
    }
  );
};

const defineTeachingAidSchema = () => {
  return Yup.lazy(
    (
      value: ElementarySchoolTeachingAidOption | HighSchoolTeachingAidOption
    ) => {
      if (value) {
        if ((value as ElementarySchoolTeachingAidOption).regular != null) {
          return Yup.object<ElementarySchoolTeachingAidOption>().shape({
            regular: Yup.boolean(),
          });
        } else if ((value as HighSchoolTeachingAidOption).regular != null) {
          return Yup.object<HighSchoolTeachingAidOption>().shape({
            regular: Yup.boolean(),
            practical: Yup.boolean(),
          });
        }
      }

      return Yup.mixed().notRequired();
    }
  );
};

const defineFoodSchema = () => {
  return Yup.lazy((value: FoodOption) => {
    if (value) {
      return Yup.object<FoodOption>().shape({
        breakfast: Yup.boolean(),
        lunch: Yup.boolean(),
        snack: Yup.boolean(),
      });
    }

    return Yup.mixed().notRequired();
  });
};

const defineFinancingRequestTest = (
  financingRequestStudentData: FinancingRequestStudentData,
  path: any,
  createError: any
) => {
  if (
    !financingRequestStudentData.transportOption &&
    !financingRequestStudentData.foodOption &&
    !financingRequestStudentData.teachingAidOption
  ) {
    return createError({
      path: path,
      message: "Potrebno je odabrati barem jednu opciju",
    });
  }
};

const defineFoodTest = (
  financingRequestStudentData: FinancingRequestStudentData,
  path: any,
  createError: any
) => {
  var hasData = false;
  if (financingRequestStudentData.foodOption) {
    const foodOption = financingRequestStudentData.foodOption;
    if (financingRequestStudentData.teachingAidOption) {
      Object.entries(financingRequestStudentData.teachingAidOption).forEach(
        (key) => {
          if (key[1]) {
            hasData = true;
          }
        }
      );
    }
    if (
      financingRequestStudentData.transportOption &&
      financingRequestStudentData.transportOption.transportOptionType
    ) {
      hasData = true;
    }

    if (
      !hasData &&
      !foodOption.breakfast &&
      !foodOption.lunch &&
      !foodOption.snack
    ) {
      return createError({
        path: path,
        message: "Potrebno je odabrati barem jednu prehranu",
      });
    }
  }
};

const defineTeachingAidTest = (
  financingRequestStudentData: FinancingRequestStudentData,
  path: any,
  createError: any
) => {
  var hasData = false;
  if (financingRequestStudentData.teachingAidOption) {
    const teachingAidOption = financingRequestStudentData.teachingAidOption;
    if (financingRequestStudentData.foodOption) {
      Object.entries(financingRequestStudentData.foodOption).forEach((key) => {
        if (key[1]) {
          hasData = true;
        }
      });
    }
    if (
      financingRequestStudentData.transportOption &&
      financingRequestStudentData.transportOption.transportOptionType
    ) {
      hasData = true;
    }

    const elementary = teachingAidOption as ElementarySchoolTeachingAidOption;
    const highSchool = teachingAidOption as HighSchoolTeachingAidOption;
    if (
      !hasData &&
      !elementary.regular &&
      !highSchool.regular &&
      !highSchool.practical
    ) {
      return createError({
        path: path,
        message: "Potrebno je odabrati barem jednu pomoć",
      });
    }
  }
};

const defineSchoolTransportTest = (
  studentsData: FinancingRequestStudentData[],
  schoolCarriers: SchoolCarrier[],
  path: any,
  createError: any
): any => {
  let schoolTransportTest = null;
  if (studentsData && studentsData.length > 0) {
    schoolCarriers.forEach((sc) => {
      let selectedSchoolTransports = 0;
      studentsData.forEach((sd: FinancingRequestStudentData) => {
        if (
          sd.transportOption &&
          sd.transportOption.transportOptionType &&
          sd.transportOption.transportOptionType ==
            TransportOptionType.SchoolCarrier
        ) {
          var schoolCarrierTransportOption = sd.transportOption as SchoolCarrierTransportOption;
          if (
            schoolCarrierTransportOption.vehicleType == sc.vehicleType &&
            schoolCarrierTransportOption.registrationPlate ==
              sc.registrationPlate
          ) {
            selectedSchoolTransports++;
          }
        }
      });

      if (selectedSchoolTransports != sc.numberOfStudents) {
        schoolTransportTest = createError({
          path: "",
          message: `Vozilom ${sc.registrationPlate} mora se voziti ${sc.numberOfStudents} učenika.`,
        });
      }
    });
  }
  return schoolTransportTest;
};

const defineAuthorizedTransportTest = (
  studentsData: FinancingRequestStudentData[],
  authorizedCarriers: AuthorizedCarrier[],
  path: any,
  createError: any
): any => {
  let authorizedTransportTest = null;
  if (studentsData && studentsData.length > 0) {
    authorizedCarriers.forEach((ac) => {
      let selectedAuthorizedTransports = 0;
      studentsData.forEach((sd: FinancingRequestStudentData) => {
        if (
          sd.transportOption &&
          sd.transportOption.transportOptionType &&
          sd.transportOption.transportOptionType ==
            TransportOptionType.AuthorizedCarrier
        ) {
          var authorizedCarrierTransportOption = sd.transportOption as AuthorizedCarrierTransportOption;
          if (authorizedCarrierTransportOption.authorizedCarrierId == ac.id) {
            selectedAuthorizedTransports++;
          }
        }
      });

      if (selectedAuthorizedTransports != ac.numberOfStudents) {
        authorizedTransportTest = createError({
          path: "",
          message: `Vozilom ${ac.name} mora se voziti ${ac.numberOfStudents} učenika.`,
        });
      }
    });
  }
  return authorizedTransportTest;
};

export const getValidationConfigurations = (
  schoolCarriers: SchoolCarrier[],
  authorizedCarriers: AuthorizedCarrier[]
): ValidationConfiguration[] => {
  return [
    defineStudentDataValidationConfig(schoolCarriers, authorizedCarriers),
  ];
};
